import { currentClip, updateClipInList, updateClipWithId } from '../clip';
import { areCaptionsObsolete, markCaptionsUpdated } from '../captions';
import createStore from '../store';
import { defaultSharedAPIStore } from './constants';
import featureFlagStore from '../featureFlagStore';

import { addPendingUpdate } from '../pendingEditsStore';
import { Clip } from '@/domains/asset';
import { updateClip, updateTranslatedCaption } from '@/libs/clipContentUtil';
import { TranscriptContextData } from '@/context/TranscriptContext/TranscriptContextTypes';
import { SpeakerSegmentContextType } from '@/context/SpeakerSegmentContext/SpeakerSegmentContextTypes';
import { getCaptions } from '@/libs/sharedAPI/common';
import { getSizeConfig } from '@/libs/sharedAPI/sizeConfig/SizeConfigFactory';
import { getClipVersion } from '@/libs/clipUtils';
import { computeCaptionsForClip } from '@/App/remotion/libs/caption/caption';
import { FeatureFlagKeys } from '@/services/featureFlag';

export interface SharedAPIStore {
  transcriptStore: TranscriptContextData;
  speakerSegmentStore: SpeakerSegmentContextType;
  mainPlayerRef: HTMLVideoElement | null;
  introPlayer: HTMLVideoElement | null;
  outroPlayer: HTMLVideoElement | null;
  speakerMap: Record<
    string,
    {
      videoRef: HTMLVideoElement;
    }
  >;
  speakerImageMap: Record<
    string,
    {
      imageRef: HTMLImageElement;
    }
  >;
}

export const sharedAPI = createStore<Record<string, SharedAPIStore>>({});

export function initSharedAPI(clipId: string) {
  sharedAPI.update(data => ({ ...data, [clipId]: defaultSharedAPIStore }));
}

export function updateSharedAPIWithId(clipId: string, data: Partial<SharedAPIStore>) {
  sharedAPI.update(store => ({ ...store, [clipId]: { ...store[clipId], ...data } }));
}

export function updateSpeakerImageMap(clipId: string, speakerKey: string, imageRef: HTMLImageElement) {
  sharedAPI.update(data => ({
    ...data,
    [clipId]: {
      ...data[clipId],
      speakerImageMap: {
        ...data[clipId].speakerImageMap,
        [speakerKey]: {
          ...data[clipId].speakerImageMap[speakerKey],
          imageRef
        }
      }
    }
  }));
}

export function updateSpeakerVideoMap(clipId: string, speakerKey: string, videoRef: HTMLVideoElement) {
  sharedAPI.update(data => ({
    ...data,
    [clipId]: {
      ...data[clipId],
      speakerMap: {
        ...data[clipId].speakerMap,
        [speakerKey]: {
          ...data[clipId].speakerMap[speakerKey],
          videoRef
        }
      }
    }
  }));
}

export function updateClipConfig({
  clipId,
  clip,
  isTranslatedCaptionEdited,
  skipRemotionUpdate
}: {
  clipId: string;
  clip?: Clip;
  isTranslatedCaptionEdited?: boolean;
  skipRemotionUpdate?: boolean;
}) {
  if (!clip) {
    clip = currentClip.getSnapshot()[clipId];
  }
  let finalClip: Clip = { ...clip };

  const clipLayout = finalClip.asset_metadata.layout;

  const useRemotionCaptions = clipLayout !== 'AUDIOGRAM';

  const version = getClipVersion();

  finalClip = {
    ...finalClip,
    version
  };

  const updateCaptionsWithFastEdit = areCaptionsObsolete(clipId);

  if (updateCaptionsWithFastEdit) {
    const { captions, excludes, duration } = computeCaptionsForClip(finalClip);

    finalClip = {
      ...finalClip,
      asset_metadata: {
        ...finalClip.asset_metadata,
        captions,
        excludes,
        duration
      }
    };

    markCaptionsUpdated({ clipId });
  }

  // START - CR-4289 - captions-undo-fix
  // This code is same as in clip.ts. That is required for history handling. This is required for clip handling
  // Temp code. Will be cleaned after removal of shared api
  const showCaptions = !!finalClip.asset_metadata.subtitle;
  if (
    finalClip.asset_metadata.caption_positions === undefined &&
    finalClip.asset_metadata.caption_styles?.placement === undefined &&
    showCaptions &&
    useRemotionCaptions
  ) {
    finalClip = {
      ...finalClip,
      asset_metadata: {
        ...finalClip.asset_metadata,
        caption_positions: {
          x: '2.5%',
          y: '82%',
          width: '95%',
          height: '18%'
        } as any
      }
    };
  }

  const sizeConfig = getSizeConfig(finalClip.asset_metadata.size, finalClip.id, finalClip.asset_metadata.layout, 1);
  const shouldDeleteConfig = !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Delete_Config];

  if (!shouldDeleteConfig) {
    finalClip = {
      ...finalClip,
      asset_metadata: {
        ...finalClip.asset_metadata,
        config: {
          ...(finalClip.asset_metadata.config || ({} as any)),
          captions: getCaptions(finalClip.asset_metadata.introDuration || 0, sizeConfig, { ...finalClip }),
          remotion_captions: useRemotionCaptions,
          filename: finalClip.title
        }
      }
    };
  }

  updateClipWithId(clipId, finalClip, true, skipRemotionUpdate);
  updateClipInList(finalClip);

  const updatePromise: Promise<any> =
    isTranslatedCaptionEdited && !updateCaptionsWithFastEdit
      ? updateTranslatedCaption(currentClip.getSnapshot()[clipId])
      : updateClip(currentClip.getSnapshot()[clipId]);

  addPendingUpdate({ clipId: finalClip.id, updatePromise, version });

  return updatePromise;
}
